/**
 * Authentication Utilities
 * Handles token storage and retrieval
 */

import { STORAGE_KEYS } from './config.js';

/**
 * Get authentication token from localStorage
 * Checks both admin panel key and main app key for compatibility
 * @returns {string|null} JWT token or null
 */
export const getAuthToken = () => {
  try {
    // Try admin panel storage key first
    const adminToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (adminToken) {
      return adminToken;
    }
    
    // Fallback to main app storage key (for compatibility)
    const mainAppToken = localStorage.getItem('authToken');
    if (mainAppToken) {
      // Also store in admin key for consistency
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, mainAppToken);
      return mainAppToken;
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to get auth token from localStorage:', error);
    return null;
  }
};

/**
 * Set authentication token in localStorage
 * Stores in both admin panel key and main app key for compatibility
 * @param {string} token - JWT token
 * @returns {boolean} Success status
 */
export const setAuthToken = (token) => {
  try {
    if (token) {
      // Store in both keys for compatibility
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
      localStorage.setItem('authToken', token);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to set auth token in localStorage:', error);
    return false;
  }
};

/**
 * Remove authentication token from localStorage
 * Removes from both admin panel key and main app key
 * @returns {boolean} Success status
 */
export const removeAuthToken = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem('authToken');
    return true;
  } catch (error) {
    console.error('Failed to remove auth token from localStorage:', error);
    return false;
  }
};

/**
 * Get refresh token from localStorage
 * @returns {string|null} Refresh token or null
 */
export const getRefreshToken = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || null;
  } catch (error) {
    console.warn('Failed to get refresh token from localStorage:', error);
    return null;
  }
};

/**
 * Set refresh token in localStorage
 * @param {string} token - Refresh token
 * @returns {boolean} Success status
 */
export const setRefreshToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to set refresh token in localStorage:', error);
    return false;
  }
};

/**
 * Clear all authentication data
 * Clears from both admin panel keys and main app keys
 * @returns {boolean} Success status
 */
export const clearAuth = () => {
  try {
    // Clear admin panel keys
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    
    // Clear main app keys (for compatibility)
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    
    return true;
  } catch (error) {
    console.error('Failed to clear auth data from localStorage:', error);
    return false;
  }
};

/**
 * Check if user is authenticated (with token expiration check)
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = getValidAuthToken();
  return token !== null;
};

/**
 * Get authorization header value
 * Only returns header if token is valid (not expired)
 * @returns {string|null} Authorization header value or null
 */
export const getAuthorizationHeader = () => {
  const token = getValidAuthToken();
  return token ? `Bearer ${token}` : null;
};

/**
 * Decode JWT token without verification (for client-side expiration check)
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded token payload or null
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null;
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn('Failed to decode token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    
    // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch (error) {
    console.warn('Failed to check token expiration:', error);
    return true;
  }
};

/**
 * Get user data from localStorage
 * Also merges permissions from JWT token if available
 * @returns {Object|null} User data object or null
 */
export const getUserData = () => {
  try {
    let userData = null;
    
    // Try admin panel storage key first
    const userDataStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (userDataStr) {
      userData = JSON.parse(userDataStr);
    } else {
      // Fallback to main app storage key (for compatibility)
      const mainAppUserData = localStorage.getItem('userData');
      if (mainAppUserData) {
        userData = JSON.parse(mainAppUserData);
      }
    }
    
    // If userData exists but doesn't have permissions, try to get them from token
    if (userData && (!userData.permissions || userData.permissions.length === 0)) {
      const token = getValidAuthToken();
      if (token) {
        const decoded = decodeToken(token);
        if (decoded && decoded.permissions) {
          userData.permissions = decoded.permissions;
        }
      }
    }
    
    return userData;
  } catch (error) {
    console.warn('Failed to get user data from localStorage:', error);
    return null;
  }
};

/**
 * Set user data in localStorage
 * @param {Object} userData - User data object
 * @returns {boolean} Success status
 */
export const setUserData = (userData) => {
  try {
    if (userData) {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
      // Also store in main app key for compatibility
      localStorage.setItem('userData', JSON.stringify(userData));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to set user data in localStorage:', error);
    return false;
  }
};

/**
 * Remove user data from localStorage
 * Removes from both admin panel key and main app key
 * @returns {boolean} Success status
 */
export const removeUserData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem('userData');
    return true;
  } catch (error) {
    console.error('Failed to remove user data from localStorage:', error);
    return false;
  }
};

/**
 * Persist token and user fields from login redirect query params (synchronous).
 * Safe to call on every route render during auth handoff.
 * @param {string} [search] - location.search
 * @returns {boolean} True when a valid token was hydrated from the URL
 */
export const hydrateAuthFromSearchParams = (search = '') => {
  try {
    const params = new URLSearchParams(search);
    const token = params.get('token');
    if (!token || isTokenExpired(token)) return false;

    setAuthToken(token);

    const name = params.get('name');
    const role = params.get('role');
    const email = params.get('email');
    const status = params.get('status');
    const access_mode = params.get('access_mode');
    const trial_expired = params.get('trial_expired');
    if (name || email || role || status || access_mode || trial_expired) {
      setUserData({
        name: name || null,
        role: role || null,
        email: email || null,
        status: status || null,
        access_mode: access_mode || null,
        trial_expired: trial_expired === '1' || trial_expired === 'true',
      });
    }

    return true;
  } catch (error) {
    console.warn('Failed to hydrate auth from URL:', error);
    return false;
  }
};

/**
 * Get authentication token with expiration check
 * @returns {string|null} Valid token or null if expired/missing
 */
export const getValidAuthToken = () => {
  const token = getAuthToken();
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    // Token expired, clear it
    clearAuth();
    return null;
  }
  
  return token;
};